import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Volume2, Mic, Brain, Eye, Compass, GraduationCap, Stethoscope, Users, 
  ClipboardList, Layers, Activity, CreditCard, MessageCircle, Box, TrendingUp, 
  Bot, Sun, Video, Baby, Briefcase, Waves, Music, Heart, Cpu, BarChart2, 
  Zap, ClipboardCheck, MapPin, Gamepad2, Play, Download, Lock, CheckCircle2,
  Sparkles, Monitor, Smartphone, ShieldCheck, UserCheck, Grid
} from 'lucide-react';
import { AACModule } from './modules/AACModule';
import { SpeechTherapyModule } from './modules/SpeechTherapyModule';
import { AutismABAModule } from './modules/AutismABAModule';
import { SignLanguageModule } from './modules/SignLanguageModule';
import { BrailleModule } from './modules/BrailleModule';
import { StudentIEPModule } from './modules/StudentIEPModule';
import { TherapistSOAPModule } from './modules/TherapistSOAPModule';
import { TeacherClassroomModule } from './modules/TeacherClassroomModule';
import { AssessmentModule } from './modules/AssessmentModule';
import { SchoolERPModule } from './modules/SchoolERPModule';
import { HospitalRehabModule } from './modules/HospitalRehabModule';
import { NDISBillingModule } from './modules/NDISBillingModule';
import { ParentCommModule } from './modules/ParentCommModule';
import { InventoryModule } from './modules/InventoryModule';
import { AnalyticsBIModule } from './modules/AnalyticsBIModule';
import { AICopilotModule } from './modules/AICopilotModule';
import { SensoryRoomModule } from './modules/SensoryRoomModule';
import { TeleRehabModule } from './modules/TeleRehabModule';
import { EarlyInterventionModule } from './modules/EarlyInterventionModule';
import { VocationalLivingModule } from './modules/VocationalLivingModule';
import { HydrotherapyModule } from './modules/HydrotherapyModule';
import { MusicTherapyModule } from './modules/MusicTherapyModule';
import { AnimalTherapyModule } from './modules/AnimalTherapyModule';
import { ProstheticsModule } from './modules/ProstheticsModule';
import { VBMAPPModule } from './modules/VBMAPPModule';
import { CoreVocabAIModule } from './modules/CoreVocabAIModule';
import { FIMScoringModule } from './modules/FIMScoringModule';
import { EVVGPSModule } from './modules/EVVGPSModule';
import { GamifiedNeuroModule } from './modules/GamifiedNeuroModule';
import { TransitionMatrixModule } from './modules/TransitionMatrixModule';
import { UserSession } from '../AppShell';
import { DEMO_STUDENTS, DemoStudent, getAccessibleStudents } from '../data/demoStudents';
import { TreatmentAuditTracker } from './TreatmentAuditTracker';

interface ModuleExplorerProps {
  initialModule?: string;
  currentUser?: UserSession | null;
}

export const ModuleExplorer: React.FC<ModuleExplorerProps> = ({ initialModule = 'aac', currentUser }) => {
  const { t } = useTranslation();
  const [activeModule, setActiveModule] = useState(initialModule);
  const [explorerTab, setExplorerTab] = useState<'modules' | 'audit_tracker'>('modules');
  
  const accessibleStudents = getAccessibleStudents(currentUser);
  const [selectedStudentId, setSelectedStudentId] = useState(accessibleStudents[0]?.id || DEMO_STUDENTS[0].id);
  const [downloadSuccess, setDownloadSuccess] = useState('');

  // Keep selectedStudentId valid when currentUser changes
  useEffect(() => {
    const freshAccessible = getAccessibleStudents(currentUser);
    if (!freshAccessible.some(s => s.id === selectedStudentId)) {
      setSelectedStudentId(freshAccessible[0]?.id || DEMO_STUDENTS[0].id);
    }
  }, [currentUser]);

  const activeStudent: DemoStudent = accessibleStudents.find(s => s.id === selectedStudentId) || accessibleStudents[0] || DEMO_STUDENTS[0];
  const isDirectStudentLogin = currentUser?.role === 'STUDENT';

  const modules = [
    { id: 'aac', label: t('mod_aac'), filePrefix: 'AAC_SpeechSynthesizer', icon: Volume2, category: 'Assistive Communication' },
    { id: 'speech-therapy', label: t('mod_speech'), filePrefix: 'SpeechTherapy_Articulation', icon: Mic, category: 'Clinical Therapy' },
    { id: 'autism', label: t('mod_autism'), filePrefix: 'Autism_ABA_Behavior', icon: Brain, category: 'Behavioral Health' },
    { id: 'sign-language', label: t('mod_sign'), filePrefix: 'SignLanguage_Gestures', icon: Eye, category: 'Visual / Hearing' },
    { id: 'braille', label: t('mod_braille'), filePrefix: 'Braille_Tactile_Learning', icon: Compass, category: 'Visual Impairment' },
    { id: 'student', label: t('mod_student'), filePrefix: 'Student_IEP_Progress', icon: GraduationCap, category: 'Special Education' },
    { id: 'therapist', label: t('mod_therapist'), filePrefix: 'Therapist_SOAP_Notes', icon: Stethoscope, category: 'Clinical Operations' },
    { id: 'teacher', label: t('mod_teacher'), filePrefix: 'Teacher_Classroom_Lesson', icon: Users, category: 'School Education' },
    { id: 'assessment', label: t('mod_assessment'), filePrefix: 'Diagnostic_Clinical_Assessment', icon: ClipboardList, category: 'Evaluations' },
    { id: 'school-erp', label: t('mod_erp'), filePrefix: 'SpecialSchool_ERP_Timetable', icon: Layers, category: 'Administration' },
    { id: 'hospital', label: t('mod_hospital'), filePrefix: 'RehabHospital_PatientCare', icon: Activity, category: 'Inpatient Care' },
    { id: 'billing', label: t('mod_billing'), filePrefix: 'NDIS_Clinical_Billing', icon: CreditCard, category: 'Finance' },
    { id: 'communication', label: t('mod_comm'), filePrefix: 'ParentComm_Messenger', icon: MessageCircle, category: 'Family Engagement' },
    { id: 'inventory', label: t('mod_inventory'), filePrefix: 'AssistiveDevice_Inventory', icon: Box, category: 'Resources' },
    { id: 'analytics', label: t('mod_analytics'), filePrefix: 'ExecutiveAnalytics_BI', icon: TrendingUp, category: 'Reporting' },
    { id: 'ai', label: t('mod_ai'), filePrefix: 'AICopilot_ReportSummarizer', icon: Bot, category: 'Intelligence' },
    { id: 'sensory-room', label: t('mod_sensory'), filePrefix: 'SensoryRoom_Snoezelen_Controller', icon: Sun, category: 'Environmental' },
    { id: 'tele-rehab', label: t('mod_tele'), filePrefix: 'TeleRehab_RemoteVideo_Therapy', icon: Video, category: 'Tele-Health' },
    { id: 'early-intervention', label: t('mod_early'), filePrefix: 'EarlyIntervention_Developmental_Tracker', icon: Baby, category: 'Pediatrics' },
    { id: 'vocational-living', label: t('mod_vocal'), filePrefix: 'Vocational_IndependentLiving_Skills', icon: Briefcase, category: 'Adult Rehab' },
    { id: 'hydrotherapy', label: t('mod_hydro'), filePrefix: 'Hydrotherapy_AquaticRehab_Controller', icon: Waves, category: 'Physical Rehab' },
    { id: 'music-therapy', label: t('mod_music'), filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation', icon: Music, category: 'Neuro-Rehab' },
    { id: 'animal-therapy', label: t('mod_animal'), filePrefix: 'AnimalAssisted_Therapy_PetCare', icon: Heart, category: 'Emotional' },
    { id: 'prosthetics', label: t('mod_prosthetics'), filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting', icon: Cpu, category: 'Bionics' },
    { id: 'vbmapp-assessment', label: t('mod_vbmapp'), filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol', icon: BarChart2, category: 'Behavioral Health' },
    { id: 'core-vocab-ai', label: t('mod_core_vocab'), filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor', icon: Zap, category: 'AAC AI' },
    { id: 'fim-weefim', label: t('mod_fim'), filePrefix: 'FIM_WeeFIM_Clinical_Scoring', icon: ClipboardCheck, category: 'Clinical Rehab' },
    { id: 'evv-gps-tracker', label: t('mod_evv'), filePrefix: 'EVV_GPS_Caregiver_CheckIn', icon: MapPin, category: 'Compliance' },
    { id: 'gamified-neuro', label: t('mod_neuro'), filePrefix: 'Gamified_WebGL_Neuro_Rehab', icon: Gamepad2, category: 'Gamified Rehab' },
    { id: 'transition-matrix', label: t('mod_transition'), filePrefix: 'Secondary_Transition_Vocational_Matrix', icon: Compass, category: 'Post-Secondary' }
  ];

  // Sync activeModule whenever initialModule prop changes
  useEffect(() => {
    if (initialModule && modules.some(m => m.id === initialModule)) {
      setActiveModule(initialModule);
    }
  }, [initialModule]);

  const isModuleEnabledForTenant = (moduleId: string) => {
    return true; // All 30 modules are 100% enabled for demo & production
  };

  const currentMod = modules.find((m) => m.id === activeModule) || modules[0];

  const handleDownloadApp = (filename: string, name: string) => {
    setDownloadSuccess(`Downloading ${name}... check your downloads folder!`);
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setDownloadSuccess(''), 4000);
  };

  const renderActiveModuleComponent = () => {
    switch (activeModule) {
      case 'aac': return <AACModule />;
      case 'speech-therapy': return <SpeechTherapyModule />;
      case 'autism': return <AutismABAModule />;
      case 'sign-language': return <SignLanguageModule />;
      case 'braille': return <BrailleModule />;
      case 'student': return <StudentIEPModule />;
      case 'therapist': return <TherapistSOAPModule />;
      case 'teacher': return <TeacherClassroomModule />;
      case 'assessment': return <AssessmentModule />;
      case 'school-erp': return <SchoolERPModule />;
      case 'hospital': return <HospitalRehabModule />;
      case 'billing': return <NDISBillingModule />;
      case 'communication': return <ParentCommModule />;
      case 'inventory': return <InventoryModule />;
      case 'analytics': return <AnalyticsBIModule />;
      case 'ai': return <AICopilotModule />;
      case 'sensory-room': return <SensoryRoomModule />;
      case 'tele-rehab': return <TeleRehabModule />;
      case 'early-intervention': return <EarlyInterventionModule />;
      case 'vocational-living': return <VocationalLivingModule />;
      case 'hydrotherapy': return <HydrotherapyModule />;
      case 'music-therapy': return <MusicTherapyModule />;
      case 'animal-therapy': return <AnimalTherapyModule />;
      case 'prosthetics': return <ProstheticsModule />;
      case 'vbmapp-assessment': return <VBMAPPModule />;
      case 'core-vocab-ai': return <CoreVocabAIModule />;
      case 'fim-weefim': return <FIMScoringModule />;
      case 'evv-gps-tracker': return <EVVGPSModule />;
      case 'gamified-neuro': return <GamifiedNeuroModule />;
      case 'transition-matrix': return <TransitionMatrixModule />;
      default: return <AACModule />;
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Student-First Global Context Selection Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-4 rounded-2xl border border-blue-500/30 flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-500/20 text-blue-300 rounded-xl border border-blue-400/30">
            <UserCheck className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <div className="text-xs font-black text-white flex items-center gap-2">
              <span>{isDirectStudentLogin ? 'Student Session Active:' : 'Active Student Profile Selected:'}</span>
              <span className="text-amber-300 font-extrabold">{activeStudent.name} ({activeStudent.rollId})</span>
            </div>
            <div className="text-[11px] text-blue-200">
              Disability Category: <span className="font-bold text-white">{activeStudent.disabilityCategory}</span> • IEP Target: <span className="font-bold text-emerald-300">{activeStudent.iepGoal}</span>
            </div>
          </div>
        </div>

        {/* Change Student Dropdown (ONLY visible for Admin / Therapist roles; HIDDEN for Student Logins) */}
        {!isDirectStudentLogin ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-blue-200 font-bold hidden sm:inline">
              {currentUser?.role === 'SUPER_ADMIN' ? 'Change Student (All Tenants):' : `School Students (${currentUser?.tenant || 'School'}):`}
            </span>
            <select 
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="px-3 py-1.5 bg-slate-950 border border-blue-400/50 rounded-xl font-bold text-xs text-blue-300 focus:ring-2 focus:ring-blue-500"
            >
              {accessibleStudents.map(st => (
                <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-xl text-xs font-extrabold flex items-center gap-1.5 shrink-0">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Authenticated Student Portal</span>
          </div>
        )}
      </div>

      {/* Workspace View Switcher (All 30 Modules vs Treatment Audit Tracker) */}
      <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setExplorerTab('modules')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              explorerTab === 'modules' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Grid className="w-4 h-4 text-amber-400" />
            <span>Interactive 30-Module Suite</span>
          </button>

          <button
            onClick={() => setExplorerTab('audit_tracker')}
            className={`px-4 py-2.5 rounded-xl font-extrabold text-xs transition flex items-center gap-2 ${
              explorerTab === 'audit_tracker' 
                ? 'bg-emerald-600 text-white shadow-md' 
                : 'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100'
            }`}
          >
            <Activity className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>Live Clinical Treatment Audit Tracker</span>
          </button>
        </div>
      </div>

      {downloadSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm animate-fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          {downloadSuccess}
        </div>
      )}

      {/* VIEW 1: INTERACTIVE 30-MODULE SUITE */}
      {explorerTab === 'modules' && (
        <>
          {/* Top 30-Module Selector Grid */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Select Any Module to Launch (All 30 Enabled for Demo)
              </h3>
              <span className="text-xs font-bold text-blue-600">30 Authorized Modules</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {modules.map((mod) => {
                const Icon = mod.icon;
                const enabled = isModuleEnabledForTenant(mod.id);
                const isActive = activeModule === mod.id;

                return (
                  <button
                    key={mod.id}
                    onClick={() => setActiveModule(mod.id)}
                    className={`p-3 rounded-xl border text-left transition flex flex-col justify-between h-20 cursor-pointer ${
                      isActive 
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-300' 
                        : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon className={`w-4 h-4 ${isActive ? 'text-amber-300' : 'text-blue-600'}`} />
                    </div>
                    <div className="text-[11px] font-extrabold leading-tight line-clamp-2">
                      {mod.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Active Module Container */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 space-y-6">
            
            {/* Active Module Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
                  {currentMod.category}
                </span>
                <h2 className="text-2xl font-black text-slate-900 mt-1 flex items-center gap-2">
                  {currentMod.label}
                </h2>
                <p className="text-xs text-slate-500">Active Patient Profile: <strong className="text-slate-900">{activeStudent.name}</strong> ({activeStudent.rollId})</p>
              </div>

              {/* Standalone Installers Bar */}
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-xl border border-slate-200 text-xs">
                <span className="font-bold text-slate-700 hidden sm:inline">Install Apps:</span>
                <button
                  onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_Windows_Setup.exe`, `${currentMod.label} Windows App`)}
                  className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-[11px] rounded-lg transition flex items-center gap-1 shadow-sm"
                >
                  <Monitor className="w-3.5 h-3.5" /> Windows
                </button>
                <button
                  onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_macOS.dmg`, `${currentMod.label} macOS App`)}
                  className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-[11px] rounded-lg transition flex items-center gap-1 shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> macOS
                </button>
                <button
                  onClick={() => handleDownloadApp(`AbilityOS_${currentMod.filePrefix}_Android.apk`, `${currentMod.label} Android App`)}
                  className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-lg transition flex items-center gap-1 shadow-sm"
                >
                  <Smartphone className="w-3.5 h-3.5" /> Android
                </button>
              </div>
            </div>

            {/* Active Module Renderer */}
            <div>
              {renderActiveModuleComponent()}
            </div>

          </div>
        </>
      )}

      {/* VIEW 2: LIVE CLINICAL TREATMENT AUDIT TRACKER */}
      {explorerTab === 'audit_tracker' && (
        <TreatmentAuditTracker />
      )}

    </div>
  );
};
